import { useRef } from "react";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { useControlled } from "@exonn/hooks";
import { css, ThemeProvider } from "@emotion/react";
import { Text } from "src/components/Text/Text.tsx";
import {
  arrow,
  autoUpdate,
  offset,
  useFloating,
  FloatingArrow,
  useRole,
  useInteractions,
  flip,
  FloatingPortal,
  shift,
  useHover,
  useDismiss,
} from "@floating-ui/react";

import type { Placement, UseHoverProps } from "@floating-ui/react";
import type { OpenChangeReason } from "@floating-ui/react";
import type { ReactNode, CSSProperties, HTMLAttributes } from "react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";

type TooltipV2BaseProps = {
  children: ReactNode;
  content: string | ReactNode;
  open?: boolean;
  placement?: Placement;
  delay?: UseHoverProps["delay"];
  defaultOpen?: boolean;
  disabled?: boolean;
  cursor?: CSSProperties["cursor"];
  usePortal?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, "content">;

type ControlledProps = {
  open: boolean;
  onClose(): void;
};

type UncontrolledProps = {
  open?: never;
  onClose?(): void;
};

type TooltipV2Props = TooltipV2BaseProps &
  (ControlledProps | UncontrolledProps);

const ARROW_HEIGHT = 5;
const ARROW_WIDTH = 11;
const GAP = 8;

export const TooltipV2 = (props: TooltipV2Props) => {
  const {
    children,
    content,
    open: openProp,
    delay,
    defaultOpen,
    disabled = false,
    placement = "top",
    // todo: make 'help' cursor default one
    cursor = "default",
    usePortal = true,
    onClose,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useControlled({
    name: "TooltipV2",
    state: "isOpen",
    controlled: openProp,
    default: defaultOpen,
  });
  const arrowRef = useRef(null);

  const onOpenChange = (open: boolean, _: Event, reason: OpenChangeReason) => {
    setIsOpen(open);
    (reason === "escape-key" || reason === "outside-press") && onClose?.();
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement,
    middleware: [
      flip(),
      shift({
        padding: 5,
      }),
      offset(ARROW_HEIGHT + GAP),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const role = useRole(context);
  const dismiss = useDismiss(context);
  const hover = useHover(context, {
    enabled: !disabled,
    delay,
  });

  const interactions = [hover, role, dismiss];

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions);
  const theme = useNonConflictingTheme();

  const TooltipContent = (
    <ThemeProvider theme={theme}>
      <div
        css={tooltipStyles}
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
      >
        <div css={contentStyles}>
          <Text css={textStyles}>{content}</Text>
        </div>

        <FloatingArrow
          height={ARROW_HEIGHT}
          width={ARROW_WIDTH}
          fill="white"
          tipRadius={2}
          ref={arrowRef}
          context={context}
        />
      </div>
    </ThemeProvider>
  );

  return (
    <div
      css={containerStyles({ cursor })}
      {...rest}
      {...getReferenceProps()}
      ref={refs.setReference}
    >
      {children}

      {isOpen &&
        !disabled &&
        (usePortal ? (
          <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
            {TooltipContent}
          </FloatingPortal>
        ) : (
          TooltipContent
        ))}
    </div>
  );
};

const textStyles = css`
  user-select: none;
  white-space: pre-line;
`;

const contentStyles = css`
  display: flex;
  flex-direction: column;
`;

const containerStyles = ({
  cursor,
}: {
  cursor: CSSProperties["cursor"];
}) => css`
  width: fit-content;
  height: fit-content;
  outline: none;
  display: flex;
  cursor: ${cursor};
`;

const tooltipStyles = (theme: ExactTheme) => css`
  position: relative;
  z-index: ${theme.zIndices.tooltips};
  background-color: white;
  border-radius: ${theme.borderRadiusV2.primary};
  color: ${theme.colorsV2.grey[800]};
  box-shadow: ${theme.shadowV2.primary};
  padding: 10px;
  font-size: ${theme.fontSizesV2.body};
  max-width: 300px;
`;
